/* eslint-disable react-hooks/exhaustive-deps */
import { useGetInviteCode, useGetInviteShare } from "@/features/user-center/services/agent";
import { useEffect, useRef } from "react";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import {
  inviteCodeAtom,
  shareConfigDataAtom,
  setInviteCodeAtom,
  setShareConfigDataAtom,
  downloadPosterRefAtom
} from "@/store/referral";
import { isLoginAtom } from "@/store";
import { useNormalizedLocale } from '@/hooks/use-normalized-locale';
import type { PosterApi } from '@/features/referral/components/base/Poster';
import { useTranslations } from 'next-intl';
import { Env } from "@/libs/env";

// 全局标识：确保只渲染一次隐藏 Poster
let isDownloadPosterMounted = false;

type UseShareOptions = {
  /** 是否在调用 hook 时立即发起接口请求，默认 true */
  fetchOnMount?: boolean;
};

export default function useShare(options: UseShareOptions = {}) {
  const t = useTranslations('referral');
  const region = Env.REGION;
  const { fetchOnMount = true } = options;
  const isLogin = useAtomValue(isLoginAtom);
  const { data: shareConfigData, isLoading } = useGetInviteShare.useQueryHook({
    params: {},
    queryConfig: { enabled: fetchOnMount },
  });
  const { data: inviteCodeData, isLoading: isInviteCodeLoading } = useGetInviteCode.useQueryHook({
    params: {},
    queryConfig: { enabled: fetchOnMount && isLogin },
  });
  const locale = useNormalizedLocale();
  const [downloadPosterRef, storeDownloadPosterRef] = useAtom(downloadPosterRefAtom);
  const localPosterRef = useRef<PosterApi>(null);

  // 从 store 读取
  const cachedInviteCode = useAtomValue(inviteCodeAtom);
  const cachedShareConfigData = useAtomValue(shareConfigDataAtom);

  // 设置方法
  const setInviteCode = useSetAtom(setInviteCodeAtom);
  const setShareConfigData = useSetAtom(setShareConfigDataAtom);

  // 将 inviteCode 和 shareConfigData 存到 referral 的 store
  useEffect(() => {
    if (inviteCodeData?.code) {
      setInviteCode(inviteCodeData?.code);
    }
    if (shareConfigData) {
      setShareConfigData(shareConfigData);
    }
  }, [inviteCodeData?.code, shareConfigData]);

  const getShareUrlPrefix = () => {
    if (typeof location === 'undefined') return '';
    // 将完整的 locale (如 'en-US') 转换为前缀 (如 'en')
    const localePrefix = locale.split('-')[0];
    return `${location?.origin}/${localePrefix}`;
  };

  const setDownloadPosterRef = (dom) => {
    localPosterRef.current = dom;
    storeDownloadPosterRef(localPosterRef);
  };

  // 渲染隐藏的下载用 Poster（单例模式）
  useEffect(() => {
    // 只在客户端且未挂载时创建
    if (typeof document === 'undefined' || isDownloadPosterMounted) {
      return;
    }

    // 只有当有必要数据时才渲染
    const posterUrl = cachedShareConfigData?.poster_image || shareConfigData?.poster_image;
    const inviteCode = cachedInviteCode || inviteCodeData?.code;

    if (!posterUrl || !inviteCode) {
      return;
    }

    // 标记已挂载
    isDownloadPosterMounted = true;


    // 清理函数
    return () => {
      isDownloadPosterMounted = false;
    };
  }, [cachedShareConfigData, shareConfigData, cachedInviteCode, inviteCodeData]);

  const code = cachedInviteCode || inviteCodeData?.code || '';
  const shareUrl = code ? `${getShareUrlPrefix()}/affiliate-invited?c=${code}` : '';

  // 获取分享文案，从前端多语言中读取
  function getShareText() {
    if (region === 'brazil') {
      return t('brazilShare', { code: inviteCodeData?.code || '' });
    }
    return t('indiaShare', { code: inviteCodeData?.code || '' });
  }

  return {
    inviteCode: cachedInviteCode || inviteCodeData?.code,
    shareUrl,
    shareConfigData: cachedShareConfigData || shareConfigData,
    activityEnabled: (cachedShareConfigData || shareConfigData)?.agent_enable,
    isLoading: isLoading || isInviteCodeLoading,
    inviteCodeData,
    downloadPosterRef,
    setDownloadPosterRef,
    // 返回 Poster 配置，供调用方渲染
    downloadPosterConfig: {
      posterUrl: cachedShareConfigData?.poster_image || shareConfigData?.poster_image,
      inviteCode: code,
      shareUrl,
    },
    // 分享文案
    shareText: getShareText() || '',
  };
}